import { useRef, useState } from 'react';
import type { FieldType } from './types';

const FIELD_TYPES: { type: FieldType; label: string; description: string }[] = [
  { type: 'text', label: 'Text', description: 'Single-line text input' },
  { type: 'number', label: 'Number', description: 'Numeric input with optional min/max' },
  { type: 'select', label: 'Dropdown', description: 'Single selection from a list' },
  { type: 'multiselect', label: 'Multi-select', description: 'Multiple selections from a list' },
  { type: 'file', label: 'File upload', description: 'Document or attachment upload' },
];

interface AddFieldButtonProps {
  onAdd: (type: FieldType) => void;
}

export function AddFieldButton({ onAdd }: AddFieldButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleAdd = (type: FieldType) => {
    onAdd(type);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-medium text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M7 1v12M1 7h12" />
        </svg>
        Add field
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
            aria-hidden="true"
          />
          <div className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden">
            {FIELD_TYPES.map(({ type, label, description }) => (
              <button
                key={type}
                type="button"
                onClick={() => handleAdd(type)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{label}</p>
                  <p className="text-xs text-slate-500">{description}</p>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
